import type { ReactNode } from 'react';
import { SORT_WORDS, type ListSort, type SortDir } from './list-sort.js';

/** A SORTABLE COLUMN HEADER (ported 2026-10-18 from the medrec console's components/list/SortHeader.tsx, its design plan's Lane 1;
 *  DESIGN_SYSTEM 7a; Rod there: "i will need to be able to sort each column"). The header's words are a button with an arrow after them: a
 *  quiet ↕ while another column sorts, ↑ or ↓ in the accent on the sorted one (its words at 600). `aria-sort` sits on the sorted column's
 *  `th` alone. Enter or Space presses it (a native button); the list decides what a press does (`pressSort`: another column ascending, the
 *  same column flipped). `extra` is anything the header carries after the button. Every list draws its headers with this one piece. */
export function SortHeader<K extends string>(p: {
  label: string; sortKey: K; sort: ListSort<K>; onSort: (key: K) => void; className?: string; testid?: string; extra?: ReactNode;
}) {
  const dir: SortDir | null = p.sort.key === p.sortKey ? p.sort.dir : null;
  return (
    <th className={p.className} aria-sort={dir === null ? undefined : dir === 'asc' ? 'ascending' : 'descending'} scope="col">
      <span className="lp-th">
        <button type="button" className={dir === null ? 'lp-sort' : 'lp-sort lp-sorted'} title={SORT_WORDS.sortBy(p.label)} data-testid={p.testid ?? `sort-${p.sortKey}`} onClick={() => p.onSort(p.sortKey)}>
          {p.label}
          <SortGlyph dir={dir} />
        </button>
        {p.extra}
      </span>
    </th>
  );
}

/** The arrow, drawn: ↕ unsorted, ↑ ascending, ↓ descending. Hidden from a screen reader, which hears `aria-sort`. */
export function SortGlyph({ dir }: { dir: SortDir | null }) {
  return (
    <svg className="lp-ar" viewBox="0 0 10 12" aria-hidden="true">
      <path d={dir === null ? 'M2.5 4.5 5 2l2.5 2.5M2.5 7.5 5 10l2.5-2.5' : dir === 'asc' ? 'M5 10V2M2 5l3-3 3 3' : 'M5 2v8M2 7l3 3 3-3'} />
    </svg>
  );
}

/** THE TOOLBAR'S SORT LINE (DESIGN_SYSTEM 7a): "Sorted by Since, oldest first, inside each group · Clear the sort", grey at the toolbar's
 *  right. Nothing while the list is in its default order (`words` null). */
export function SortedLine({ words, onClear, isDefault }: { words: string | null; onClear: () => void; isDefault: boolean }) {
  if (words === null) return null;
  return (
    <span className="lp-sorted-line" data-testid="sorted-line">
      {words}
      {isDefault ? null : (
        <>
          {' \u{00B7} '}
          <button type="button" className="lp-linkbtn" data-testid="sort-clear" onClick={onClear}>
            {SORT_WORDS.clear}
          </button>
        </>
      )}
    </span>
  );
}

/** A header with no sort: the key column (its circle i is per row) and the row's own "more" column, named for a screen reader. */
export function PlainHeader({ label, className }: { label: string; className?: string }) {
  return <th className={className} scope="col"><span className="sr">{label}</span></th>;
}
